import React, { useState, useEffect, useMemo, useRef } from 'react';

export function RadioQuestion({ question, value, onChange, allAnswers = {} }) {
  // Handle "outro" option with text input
  const [otherText, setOtherText] = useState('');
  const [selectedValue, setSelectedValue] = useState(value || '');
  const containerRef = useRef(null);

  // Resolve options - support both static arrays and dynamic functions
  const options = useMemo(() => {
    if (typeof question.options === 'function') {
      return question.options(allAnswers);
    }
    return question.options;
  }, [question.options, allAnswers]);

  // If question has allowOther, add "outro" option to the list
  const hasOtherOption = question.allowOther === true;

  // Parse incoming value if it's custom text
  useEffect(() => {
    if (value && hasOtherOption) {
      // Check if value is not in the predefined options
      const isKnownOption = options.some(opt => opt.value === value);
      if (!isKnownOption && value !== 'outro') {
        // This is custom "outro" text
        setSelectedValue('outro');
        setOtherText(value);
      } else {
        setSelectedValue(value);
        setOtherText('');
      }
    } else {
      setSelectedValue(value || '');
      setOtherText('');
    }
  }, [value, hasOtherOption, options]);

  const handleRadioChange = (optionValue) => {
    setSelectedValue(optionValue);
    if (optionValue !== 'outro') {
      setOtherText('');
      onChange(optionValue);

      // Scroll to navigation buttons after selection
      setTimeout(() => {
        scrollToNavigationButtons();
      }, 100);
    } else {
      // When "outro" is selected, wait for text input
      onChange(otherText || 'outro');
    }
  };

  const scrollToNavigationButtons = () => {
    // Find the navigation buttons container
    const questionCard = containerRef.current?.closest('.bg-white.rounded-2xl');
    if (questionCard) {
      const navButtons = questionCard.querySelector('button[class*="bg-gradient-to-r"]');
      if (navButtons) {
        // Get the footer height to offset scroll
        const footer = document.querySelector('footer');
        const footerHeight = footer ? footer.offsetHeight : 0;

        // Calculate the position to scroll to
        const navButtonsRect = navButtons.getBoundingClientRect();
        const scrollOffset = window.pageYOffset + navButtonsRect.top - (window.innerHeight - footerHeight - 100);

        // Scroll to position that shows the button above the footer with extra padding
        window.scrollTo({
          top: scrollOffset,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleOtherTextChange = (e) => {
    const text = e.target.value;
    setOtherText(text);
    // Send the actual text as the value, not "outro"
    onChange(text || 'outro');
  };

  return (
    <div ref={containerRef} className="space-y-3">
      {options.map((opt) => (
        <label
          key={opt.value}
          className="flex items-center p-3 sm:p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition"
        >
          <input
            type="radio"
            name={question.id}
            value={opt.value}
            checked={selectedValue === opt.value}
            onChange={(e) => handleRadioChange(e.target.value)}
            className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500"
          />
          <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-700 font-medium">{opt.label}</span>
        </label>
      ))}

      {/* "Outro" option if allowOther is true */}
      {hasOtherOption && (
        <div>
          <label className="flex items-start p-3 sm:p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition">
            <input
              type="radio"
              name={question.id}
              value="outro"
              checked={selectedValue === 'outro'}
              onChange={(e) => handleRadioChange(e.target.value)}
              className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 mt-0.5"
            />
            <div className="ml-2 sm:ml-3 flex-1">
              <span className="text-sm sm:text-base text-gray-700 font-medium">Outro</span>
              {selectedValue === 'outro' && (
                <input
                  type="text"
                  value={otherText}
                  onChange={handleOtherTextChange}
                  placeholder="Especifique..."
                  className="mt-2 w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </div>
          </label>
        </div>
      )}
    </div>
  );
}
